import { __rest } from 'tslib';
import React from 'react';
import { space } from 'styled-system';
import { s as styled } from './styled-112a8bd0.js';
import { S as SvgIcon } from './SvgIcon-1ee3e5bb.js';
import { t as theme } from './theme-c93653c9.js';

const getBackground = (props) => {
    if (props.link)
        return 'transparent';
    if (props.outline)
        return props.theme.colors.white;
    return props.disabled
        ? `${props.theme.colors.gray400} !important`
        : props.variant === 'primary'
            ? props.theme.brand.primary
            : props.variant === 'secondary'
                ? props.theme.brand.secondary
                : props.variant === 'tertiary'
                    ? props.theme.brand.tertiary
                    : props.variant === 'normal'
                        ? props.theme.colors.gray300
                        : props.variant === 'danger'
                            ? props.theme.colors.red
                            : props.variant === 'disabled'
                                ? `${props.theme.colors.gray400} !important`
                                : props.theme.colors.gray200;
};
const getColor = (props) => {
    if (props.variant === 'tertiary')
        return props.theme.colors.text;
    if (props.tag && !props.outline && !props.link)
        return props.theme.colors.white + '!important';
    if (props.outline || props.link) {
        return props.variant === 'primary'
            ? props.theme.brand.primary
            : props.variant === 'secondary'
                ? props.theme.brand.secondary
                : props.variant === 'danger'
                    ? props.theme.colors.red
                    : props.variant === 'disabled'
                        ? `${props.theme.colors.gray600} !important`
                        : props.theme.colors.text;
    }
    if (props.disabled)
        return `${theme.colors.gray600} !important`;
    return props.variant && props.variant !== 'normal' && !props.link
        ? theme.colors.white
        : theme.colors.gray900;
};
const StyledButton = styled.button((props) => {
    return `
    cursor: pointer;
    border-radius: 4px;
    overflow: hidden;
    ${props.size === 'lg'
        ? `
        line-height: 1.5;
        padding: .5rem 1rem;`
        : `
        line-height: 1rem;
        padding: .365rem .75rem;`}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    background-color: ${getBackground(props)};
    border-color: ${props.outline ? getColor(props) : getBackground(props)};
    color: ${getColor(props)};
    border-width: 1px;
    border-style: solid;
    user-select: none;
    text-decoration: none;
    flex-direction: ${props.flexDirection};
    position: relative;
    ${props.block ? `width: 100%;` : ''}
    ${props.disabled && 'pointer-events: none;'}
    &:focus {
      outline: 0;
    }
    `;
}, space);
const Spacer = styled.div `
  width: 0.4rem;
  height: 0.4rem;
`;
const LoadingWrapper = styled.div((props) => `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${getBackground(props)};
  .pulse {
    position: relative;
  left: -9999px;
  width: 4px;
  height: 4px;
  border-radius: 5px;
  background-color: ${getColor(props)};
  color: ${getColor(props)};
  box-shadow: 9999px 0 0 -5px ${getColor(props)};
  animation: dot-pulse 1.5s infinite linear;
  animation-delay: 0.25s;

  &:before,
  &:after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${getColor(props)};
    color: ${getColor(props)};
  }
  &:before {
    box-shadow: 9988px 0 0 -5px ${getColor(props)};
    animation: dot-pulse-before 1.5s infinite linear;
    animation-delay: 0s;
  }
  &:after {
    box-shadow: 10010px 0 0 -5px ${getColor(props)};
    animation: dot-pulse-after 1.5s infinite linear;
    animation-delay: 0.5s;
  }
  @keyframes dot-pulse-before {
    0% {
      box-shadow: 9988px 0 0 -5px ${getColor(props)};
    }
    30% {
      box-shadow: 9988px 0 0 2px ${getColor(props)};
    }
    60%,
    100% {
      box-shadow: 9988px 0 0 -5px ${getColor(props)};
    }
  }

  @keyframes dot-pulse {
    0% {
      box-shadow: 9999px 0 0 -5px ${getColor(props)};
    }
    30% {
      box-shadow: 9999px 0 0 2px ${getColor(props)};
    }
    60%,
    100% {
      box-shadow: 9999px 0 0 -5px ${getColor(props)};
    }
  }

  @keyframes dot-pulse-after {
    0% {
      box-shadow: 10010px 0 0 -5px ${getColor(props)};
    }
    30% {
      box-shadow: 10010px 0 0 2px ${getColor(props)};
    }
    60%,
    100% {
      box-shadow: 10010px 0 0 -5px ${getColor(props)};
    }
  }
  }
`);
const Button = React.forwardRef((props, ref) => {
    const { outline, variant, children, block, tag, icon, disabled, flexDirection = 'row', loading, onClick } = props, others = __rest(props, ["outline", "variant", "children", "block", "tag", "icon", "disabled", "flexDirection", "loading", "onClick"]);
    const ButtonTag = StyledButton.withComponent(tag ? tag : 'button');
    return (React.createElement(ButtonTag, Object.assign({ ref: ref, block: block, variant: variant, outline: outline, flexDirection: flexDirection, disabled: disabled, tag: tag, onClick: loading ? undefined : onClick }, others),
        icon && React.createElement(SvgIcon, Object.assign({}, icon)),
        icon && children && React.createElement(Spacer, { className: "spacer" }),
        children,
        loading && (React.createElement(LoadingWrapper, { variant: variant, outline: outline },
            React.createElement("div", { className: "pulse" })))));
});
Button.displayName = 'Button';

export { Button as B };
